import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import moment from 'moment-timezone';
import airbnbApi from './api';
import {
  BULK_STEPS, CodeSelect, ErrorAlert, FlowASteps, Notice, PageLoader, STEP_LABELS, StatusBadge, formatTime, useMasterData
} from './common';

const MIN_AIRBNB_IMAGES = 5;
const AMOUNT_TYPES = ['percentage', 'per_night', 'fixed_amount'];
const NO_SHOW_AMOUNT_TYPES = ['percentage', 'per_night'];

const Info = ({ label, children, md = 3 }) => (
  <Col md={md} className="margbot10">
    <div className="fnt12 text-muted">{label}</div>
    <div>{children || '—'}</div>
  </Col>
);

// ---------------------------------------------------------------- images

const emptyImage = () => ({
  url: '', category: '', caption: '', display_priority: 0,
  // Must be the real change date: moving it forward makes NextPax re-download the image
  last_updated_on: moment().format('YYYY-MM-DD')
});

const Images = ({ propertyId, onChange, roomName }) => {
  const [images, setImages] = useState(null);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncSummary, setSyncSummary] = useState(null);

  const load = useCallback(() => {
    airbnbApi.listImages(propertyId).then(setImages).catch(setError);
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);

  const add = e => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    airbnbApi.createImage(propertyId, { ...draft, display_priority: parseInt(draft.display_priority, 10) || 0 })
      .then(() => { setDraft(null); load(); onChange(); })
      .catch(setError)
      .finally(() => setBusy(false));
  };

  const remove = image => {
    const message = roomName
      ? 'Remove this room photo?'
      : 'Remove this hotel photo? It is shared with other channels and with every room type of this hotel.';
    if (!window.confirm(message)) return;
    airbnbApi.deleteImage(propertyId, image.id).then(() => { load(); onChange(); }).catch(setError);
  };

  const sync = () => {
    if (!window.confirm('Import this hotel\'s photos from RedDoorz? Photos added by hand are kept; RedDoorz photos that were removed there are removed here.')) return;
    setSyncing(true);
    setError(null);
    setSyncSummary(null);
    airbnbApi.syncPhotos(propertyId)
      .then(result => { setSyncSummary(result); setImages(result.images); onChange(); })
      .catch(setError)
      .finally(() => setSyncing(false));
  };

  // Imported photos whose label didn't map to a NextPax image type
  const setCategory = (image, category) => {
    setError(null);
    airbnbApi.updateImage(propertyId, image.id, { category })
      .then(updated => {
        setImages(prev => prev.map(i => (i.id === image.id ? { ...i, ...updated } : i)));
        onChange();
      })
      .catch(setError);
  };

  const count = images ? images.length : 0;
  const uncategorized = images ? images.filter(i => !i.category).length : 0;

  return (
    <Card className="margbot15 mt-2">
      <Card.Header className="bld d-flex justify-content-between align-items-center">
        <span>
          Images ({count}){' '}
          {images && count < MIN_AIRBNB_IMAGES && <Badge bg="warning" text="dark">Airbnb needs at least {MIN_AIRBNB_IMAGES}</Badge>}
          {uncategorized > 0 && <Badge bg="danger" className="ms-1">{uncategorized} need a category</Badge>}
        </span>
        <span>
          <Button size="sm" variant="outline-secondary" className="me-2" disabled={syncing} onClick={sync}>
            {syncing ? <><Spinner animation="border" size="sm" /> Syncing…</> : 'Sync from RedDoorz'}
          </Button>
        {!draft && (
          <Button size="sm" variant="outline-primary"
            onClick={() => setDraft({ ...emptyImage(), display_priority: count })}>Add image</Button>
        )}
        </span>
      </Card.Header>
      <Card.Body>
        <p className="fnt12 text-muted">
          {roomName
            ? `"${roomName}" uses its own room photos first, then all of the hotel's photos — add room photos only if you want extra ones. Hotel photos are managed on the hotel property.`
            : 'Hotel photos, shared by every channel (and by every room type of a multi-unit). The first photo is the main one.'}
        </p>
        <ErrorAlert error={error} onClose={() => setError(null)} />
        {syncSummary && (
          <Alert variant="success" dismissible onClose={() => setSyncSummary(null)} className="fnt13">
            <div className="bld">
              Synced {syncSummary.fetched} RedDoorz photo(s): {syncSummary.created} new, {syncSummary.updated} updated,
              {' '}{syncSummary.unchanged} unchanged, {syncSummary.removed} removed{syncSummary.skipped ? `, ${syncSummary.skipped} skipped (Others)` : ''}.
            </div>
            <div>
              Hotel photos: {syncSummary.hotel_photos}
              {Object.keys(syncSummary.room_photos || {}).length > 0 && (
                <> · Room photos: {Object.entries(syncSummary.room_photos).map(([room, n]) => `${room} (${n})`).join(', ')}</>
              )}
            </div>
            {syncSummary.uncategorized > 0 && (
              <div className="text-danger">
                {syncSummary.uncategorized} photo(s) have a RedDoorz label with no matching image type — choose a category below.
              </div>
            )}
            {syncSummary.unmatched_room_tags && syncSummary.unmatched_room_tags.length > 0 && (
              <div className="text-muted">
                No room category named {syncSummary.unmatched_room_tags.join(', ')} — those photos were kept as hotel photos.
              </div>
            )}
          </Alert>
        )}

        {draft && (
          <Form onSubmit={add} className="border rounded pad10 margbot15">
            <Row>
              <Col md={5} className="margbot10">
                <Form.Label className="fnt13 mb-1">URL <span className="text-danger">*</span></Form.Label>
                <Form.Control type="url" required value={draft.url} placeholder="https://…"
                  onChange={e => setDraft({ ...draft, url: e.target.value })} />
              </Col>
              <Col md={3} className="margbot10">
                <Form.Label className="fnt13 mb-1">Category <span className="text-danger">*</span></Form.Label>
                <CodeSelect category="images" required value={draft.category}
                  onChange={v => setDraft({ ...draft, category: v })} />
              </Col>
              <Col md={2} className="margbot10">
                <Form.Label className="fnt13 mb-1">Priority</Form.Label>
                <Form.Control type="number" min={0} value={draft.display_priority}
                  onChange={e => setDraft({ ...draft, display_priority: e.target.value })} />
              </Col>
              <Col md={2} className="margbot10">
                <Form.Label className="fnt13 mb-1">Last updated <span className="text-danger">*</span></Form.Label>
                <Form.Control type="date" required value={draft.last_updated_on}
                  onChange={e => setDraft({ ...draft, last_updated_on: e.target.value })} />
              </Col>
              <Col md={8} className="margbot10">
                <Form.Label className="fnt13 mb-1">Caption</Form.Label>
                <Form.Control value={draft.caption} onChange={e => setDraft({ ...draft, caption: e.target.value })} />
              </Col>
              <Col md={4} className="d-flex align-items-end margbot10">
                <Button type="submit" size="sm" disabled={busy} className="me-2">
                  {busy ? <Spinner animation="border" size="sm" /> : 'Save image'}
                </Button>
                <Button size="sm" variant="link" onClick={() => setDraft(null)}>Cancel</Button>
              </Col>
            </Row>
          </Form>
        )}

        {!images && !error && <PageLoader />}
        {images && count === 0 && <div className="text-muted">No images yet.</div>}
        {images && count > 0 && (
          <Table bordered size="sm" className="bg1 align-middle mb-0">
            <thead>
              <tr><th style={{ width: 90 }}>Preview</th><th>Priority</th><th>Category</th><th>Caption</th><th>Last updated</th><th /></tr>
            </thead>
            <tbody>
              {images.map(image => (
                <tr key={image.id}>
                  <td>
                    <a href={image.url} target="_blank" rel="noreferrer">
                      <img src={image.url} alt={image.caption || ''} style={{ width: 80, height: 55, objectFit: 'cover' }} />
                    </a>
                  </td>
                  <td>
                    {image.display_priority}
                    {images[0] && images[0].id === image.id && <Badge bg="info" className="ms-1">main</Badge>}
                    {image.inherited && <Badge bg="light" text="dark" className="ms-1">from hotel</Badge>}
                    {image.source === 'reddoorz' && <Badge bg="secondary" className="ms-1">RedDoorz</Badge>}
                    {image.room_category_name && !roomName && <Badge bg="light" text="dark" className="ms-1">{image.room_category_name}</Badge>}
                  </td>
                  <td style={{ minWidth: 170 }} className={!image.category ? 'table-danger' : ''}>
                    {image.inherited
                      ? (image.category || <span className="text-danger">not set (set on hotel)</span>)
                      : (
                        <CodeSelect category="images" value={image.category || ''} placeholder="Choose category…"
                          onChange={v => v && setCategory(image, v)} />
                      )}
                  </td>
                  <td>{image.caption || '—'}</td>
                  <td>{image.last_updated_on}</td>
                  <td>
                    {image.inherited
                      ? <span className="fnt12 text-muted">managed on hotel</span>
                      : <Button size="sm" variant="outline-danger" onClick={() => remove(image)}>Remove</Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>
    </Card>
  );
};

// ------------------------------------------------- cancellation policies

const emptyPolicy = () => ({
  name: '', is_default: false, status: 'active',
  rules: { beforeArrivalHours: 48, beforeArrivalAmount: 100, beforeArrivalAmountType: 'percentage' }
});

const numberOrNull = v => (v === '' || v === null || v === undefined ? undefined : Number(v));

const describeRules = rules => {
  if (!rules) return '—';
  const unit = type => ({ percentage: '%', per_night: ' night(s)', fixed_amount: ' (fixed)' }[type] || '');
  const parts = [];
  if (rules.beforeArrivalHours !== undefined) {
    parts.push(`${rules.beforeArrivalAmount}${unit(rules.beforeArrivalAmountType)} charged within ${rules.beforeArrivalHours}h of arrival`);
  }
  if (rules.noShowAmount !== undefined) parts.push(`no-show: ${rules.noShowAmount}${unit(rules.noShowAmountType)}`);
  return parts.length ? parts.join('; ') : JSON.stringify(rules);
};

const PolicyForm = ({ initial, onSubmit, onCancel, busy }) => {
  const [policy, setPolicy] = useState(initial);
  const rules = policy.rules || {};
  const setRule = (key, value) => setPolicy({ ...policy, rules: { ...rules, [key]: value } });

  const submit = e => {
    e.preventDefault();
    const cleanRules = Object.fromEntries(Object.entries({
      ...rules,
      beforeArrivalHours: numberOrNull(rules.beforeArrivalHours),
      beforeArrivalAmount: numberOrNull(rules.beforeArrivalAmount),
      noShowAmount: numberOrNull(rules.noShowAmount)
    }).filter(([, v]) => v !== undefined && v !== ''));
    if (cleanRules.noShowAmount === undefined) delete cleanRules.noShowAmountType;
    onSubmit({ ...policy, rules: cleanRules });
  };

  return (
    <Form onSubmit={submit} className="border rounded pad10 margbot15">
      <Row>
        <Col md={4} className="margbot10">
          <Form.Label className="fnt13 mb-1">Name <span className="text-danger">*</span></Form.Label>
          <Form.Control required value={policy.name} onChange={e => setPolicy({ ...policy, name: e.target.value })} />
        </Col>
        <Col md={2} className="margbot10">
          <Form.Label className="fnt13 mb-1">Status</Form.Label>
          <Form.Select value={policy.status} onChange={e => setPolicy({ ...policy, status: e.target.value })}>
            <option value="active">active</option>
            <option value="inactive">inactive</option>
          </Form.Select>
        </Col>
        <Col md={2} className="d-flex align-items-end margbot10">
          <Form.Check type="checkbox" id={`default-${policy.id || 'new'}`} label="Default" checked={!!policy.is_default}
            onChange={e => setPolicy({ ...policy, is_default: e.target.checked })} />
        </Col>
      </Row>
      <Row>
        <Col md={3} className="margbot10">
          <Form.Label className="fnt13 mb-1">Before arrival (hours)</Form.Label>
          <Form.Control type="number" min={0} value={rules.beforeArrivalHours ?? ''} onChange={e => setRule('beforeArrivalHours', e.target.value)} />
        </Col>
        <Col md={3} className="margbot10">
          <Form.Label className="fnt13 mb-1">Amount charged</Form.Label>
          <Form.Control type="number" min={0} value={rules.beforeArrivalAmount ?? ''} onChange={e => setRule('beforeArrivalAmount', e.target.value)} />
        </Col>
        <Col md={3} className="margbot10">
          <Form.Label className="fnt13 mb-1">Amount type</Form.Label>
          <Form.Select value={rules.beforeArrivalAmountType || 'percentage'} onChange={e => setRule('beforeArrivalAmountType', e.target.value)}>
            {AMOUNT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </Form.Select>
        </Col>
      </Row>
      <Row>
        <Col md={3} className="margbot10">
          <Form.Label className="fnt13 mb-1">No-show amount</Form.Label>
          <Form.Control type="number" min={0} value={rules.noShowAmount ?? ''} onChange={e => setRule('noShowAmount', e.target.value)} />
        </Col>
        <Col md={3} className="margbot10">
          <Form.Label className="fnt13 mb-1">No-show amount type</Form.Label>
          <Form.Select value={rules.noShowAmountType || 'percentage'} onChange={e => setRule('noShowAmountType', e.target.value)}>
            {NO_SHOW_AMOUNT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </Form.Select>
        </Col>
        <Col md={6} className="d-flex align-items-end margbot10">
          <Button type="submit" size="sm" disabled={busy} className="me-2">
            {busy ? <Spinner animation="border" size="sm" /> : 'Save policy'}
          </Button>
          <Button size="sm" variant="link" onClick={onCancel}>Cancel</Button>
        </Col>
      </Row>
    </Form>
  );
};

const CancellationPolicies = ({ propertyId, onChange }) => {
  const [policies, setPolicies] = useState(null);
  const [editing, setEditing] = useState(null); // 'new' or a policy id
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    airbnbApi.listPolicies(propertyId).then(setPolicies).catch(setError);
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);

  const save = data => {
    setBusy(true);
    setError(null);
    const { id, channel_references: _refs, ...payload } = data;
    const request = editing === 'new'
      ? airbnbApi.createPolicy(propertyId, payload)
      : airbnbApi.updatePolicy(propertyId, id, payload);
    request
      .then(() => { setEditing(null); load(); onChange(); })
      .catch(setError)
      .finally(() => setBusy(false));
  };

  const remove = policy => {
    if (!window.confirm(`Delete policy "${policy.name}"?`)) return;
    airbnbApi.deletePolicy(propertyId, policy.id).then(() => { load(); onChange(); }).catch(setError);
  };

  return (
    <Card className="margbot15 mt-2">
      <Card.Header className="bld d-flex justify-content-between align-items-center">
        Cancellation policies ({policies ? policies.length : 0})
        {!editing && <Button size="sm" variant="outline-primary" onClick={() => setEditing('new')}>Add policy</Button>}
      </Card.Header>
      <Card.Body>
        <p className="fnt12 text-muted">
          Policies belong to the hotel. The property manager's "Send cancellation policies" step sends them to NextPax
          (at most 7 per property manager).
        </p>
        <ErrorAlert error={error} onClose={() => setError(null)} />
        {editing === 'new' && <PolicyForm initial={emptyPolicy()} onSubmit={save} onCancel={() => setEditing(null)} busy={busy} />}

        {!policies && !error && <PageLoader />}
        {policies && policies.length === 0 && editing !== 'new' && <div className="text-muted">No cancellation policies yet.</div>}
        {policies && policies.length > 0 && (
          <Table bordered size="sm" className="bg1 align-middle mb-0">
            <thead><tr><th>Name</th><th>Status</th><th>Rules</th><th>NextPax ID</th><th style={{ width: 150 }} /></tr></thead>
            <tbody>
              {policies.map(policy => (editing === policy.id ? (
                <tr key={policy.id}>
                  <td colSpan={5}>
                    <PolicyForm initial={policy} onSubmit={save} onCancel={() => setEditing(null)} busy={busy} />
                  </td>
                </tr>
              ) : (
                <tr key={policy.id}>
                  <td>{policy.name}{policy.is_default && <Badge bg="info" className="ms-1">default</Badge>}</td>
                  <td><Badge bg={policy.status === 'active' ? 'success' : 'secondary'}>{policy.status}</Badge></td>
                  <td className="fnt12">{describeRules(policy.rules)}</td>
                  <td>{(policy.channel_references || {}).AIR298 || '—'}</td>
                  <td>
                    <Button size="sm" variant="outline-primary" className="me-1" disabled={!!editing}
                      onClick={() => setEditing(policy.id)}>Edit</Button>
                    <Button size="sm" variant="outline-danger" disabled={!!editing} onClick={() => remove(policy)}>Delete</Button>
                  </td>
                </tr>
              )))}
            </tbody>
          </Table>
        )}
      </Card.Body>
    </Card>
  );
};

// ---------------------------------------------------- Airbnb requirements

// NextPax content definitions for Airbnb (master data content_definitions under
// AIR298), filtered to the property's classification.
const AirbnbRequirements = ({ classification }) => {
  const definitions = useMasterData('content_definitions', 'AIR298').filter(d => {
    const applies = d.data && d.data.applicableClassifications;
    return !applies || applies.includes(classification);
  });
  if (definitions.length === 0) return null;

  return (
    <Card className="margbot15 mt-2">
      <Card.Header className="bld">Airbnb content requirements ({classification})</Card.Header>
      <Card.Body>
        <Table bordered size="sm" className="bg1 mb-0">
          <thead><tr><th>Requirement</th><th>Value</th><th>Severity</th></tr></thead>
          <tbody>
            {definitions.map(d => (
              <tr key={d.code}>
                <td>{d.name || d.code}<div className="fnt11 text-muted">{d.data.requirementDescription}</div></td>
                <td>{d.data.requirementValue}</td>
                <td>
                  {d.data.isBlockingStatus
                    ? <Badge bg="danger">blocking</Badge>
                    : d.data.isWarning ? <Badge bg="warning" text="dark">warning</Badge> : <Badge bg="secondary">info</Badge>}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );
};

// ------------------------------------------------------------- room types

// Multi-unit only: pick the hotel's room categories to onboard as NextPax unit
// types, then run steps for all of them at once.
const RoomTypes = ({ property, onChange }) => {
  const [categories, setCategories] = useState(null);
  const [units, setUnits] = useState([]);
  const [selected, setSelected] = useState([]);
  const [typeCode, setTypeCode] = useState('ROO');
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [bulkResults, setBulkResults] = useState(null);

  const load = useCallback(() => {
    airbnbApi.roomCategories(property.id).then(setCategories).catch(setError);
    airbnbApi.listUnitTypes(property.id).then(setUnits).catch(setError);
  }, [property.id]);

  useEffect(() => { load(); }, [load]);

  const toggle = categoryId => setSelected(prev => (
    prev.includes(categoryId) ? prev.filter(x => x !== categoryId) : [...prev, categoryId]
  ));

  const addSelected = () => {
    setBusy('add');
    setError(null);
    airbnbApi.createUnitTypes(property.id, selected, typeCode)
      .then(created => {
        setNotice(`Added ${created.length} room type(s).`);
        setSelected([]);
        load();
        onChange();
      })
      .catch(setError)
      .finally(() => setBusy(null));
  };

  const bulkRun = step => {
    if (!window.confirm(`Run "${STEP_LABELS[step]}" for every room type of this property?`)) return;
    setBusy(step);
    setError(null);
    airbnbApi.bulkRunUnitTypes(property.id, step)
      .then(results => { setBulkResults(results); load(); })
      .catch(setError)
      .finally(() => setBusy(null));
  };

  const eligibleFree = (categories || []).filter(c => !c.ineligible_reason && !c.unit_type_id);

  return (
    <Card className="margbot15 mt-2">
      <Card.Header className="bld">Room types (unit types) — {units.length} added</Card.Header>
      <Card.Body>
        <p className="fnt12 text-muted">
          Each room type becomes a NextPax unit type linked to this property. Sale rooms (RDSL/ORDSL) and derived
          categories are excluded because they share another category's inventory.
          {property.onboarding_mode === 'takeover' && ' For a takeover, every room type needs its own Airbnb listing ID.'}
        </p>
        <ErrorAlert error={error} onClose={() => setError(null)} />
        <Notice notice={notice} onClose={() => setNotice(null)} />

        {!categories && !error && <PageLoader />}
        {categories && (
          <>
            <Table bordered size="sm" className="bg1 align-middle">
              <thead>
                <tr><th style={{ width: 36 }} /><th>Room category</th><th>Code</th><th>Rooms</th><th>Capacity</th><th>Status</th></tr>
              </thead>
              <tbody>
                {categories.map(c => (
                  <tr key={c.id} className={c.ineligible_reason ? 'text-muted' : ''}>
                    <td>
                      <Form.Check type="checkbox" checked={selected.includes(c.id)}
                        disabled={!!c.ineligible_reason || !!c.unit_type_id} onChange={() => toggle(c.id)} />
                    </td>
                    <td>{c.name}</td>
                    <td><code>{c.category_code}</code></td>
                    <td>{c.total_rooms}</td>
                    <td>{c.room_capacity}</td>
                    <td className="fnt12">
                      {c.unit_type_id && <Link to={`/airbnb/properties/${c.unit_type_id}`}>added</Link>}
                      {!c.unit_type_id && c.ineligible_reason && <span>excluded: {c.ineligible_reason}</span>}
                      {!c.unit_type_id && !c.ineligible_reason && <span>eligible</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {eligibleFree.length > 0 && (
              <Row className="align-items-end margbot15">
                <Col md={4}>
                  <Form.Label className="fnt13 mb-1">Property type for the new room types</Form.Label>
                  <CodeSelect category="property_types" value={typeCode} onChange={setTypeCode}
                    filter={c => !(c.data && c.data.classifications) || c.data.classifications.includes('unit-type')} />
                  <Form.Text className="fnt11">ROO = room in a hotel; VIL = villa (villa complex)</Form.Text>
                </Col>
                <Col md={4}>
                  <Button size="sm" disabled={!selected.length || !!busy} onClick={addSelected}>
                    {busy === 'add' ? <Spinner animation="border" size="sm" /> : `Add selected (${selected.length})`}
                  </Button>
                </Col>
              </Row>
            )}
          </>
        )}

        {units.length > 0 && (
          <>
          <div className="table-responsive">
              <Table bordered hover size="sm" className="bg1 align-middle">
                <thead>
                  <tr><th>Room type</th><th>Type</th><th>Units</th><th>NextPax ID</th><th>Airbnb listing ID</th><th>Images</th><th>Airbnb build</th></tr>
                </thead>
                <tbody>
                  {units.map(u => (
                    <tr key={u.id}>
                      <td><Link to={`/airbnb/properties/${u.id}`}>{u.name}</Link></td>
                      <td>{u.type_code}</td>
                      <td>{u.number_of_units}</td>
                      <td className="fnt12">{u.nextpax_property_id || <span className="text-muted">not created</span>}</td>
                      <td className={u.listing_id_required && !u.airbnb_listing_id ? 'text-danger' : ''}>
                        {u.airbnb_listing_id || (u.listing_id_required ? 'required' : 'not needed')}
                      </td>
                      <td className={u.images_count < MIN_AIRBNB_IMAGES ? 'text-danger' : ''}>{u.images_count}</td>
                      <td><StatusBadge status={u.airbnb_build_status} /></td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            <div className="bld margbot5">Run a step for all room types</div>
            <div className="fnt12 text-muted margbot5">Run the same step on this property first — room types wait for it.</div>
            {BULK_STEPS.map(step => (
              <Button key={step} size="sm" variant="outline-secondary" className="me-2 mb-2"
                disabled={!!busy} onClick={() => bulkRun(step)}>
                {busy === step ? <Spinner animation="border" size="sm" /> : STEP_LABELS[step]}
              </Button>
            ))}
            {bulkResults && (
              <div className="table-responsive">
                <Table bordered size="sm" className="bg1 margtop10 mb-0">
                  <thead><tr><th>Room type</th><th>Status</th><th>Details</th></tr></thead>
                  <tbody>
                    {bulkResults.map(r => (
                      <tr key={r.property_id}>
                        <td>{r.name || r.property_id}</td>
                        <td><StatusBadge status={r.status} /></td>
                        <td className="fnt12">
                          {r.reasons && r.reasons.join('; ')}
                          {r.message && `${r.code ? `${r.code}: ` : ''}${r.message}`}
                          {r.request_id && `Request ${r.request_id}`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </>
        )}
      </Card.Body>
    </Card>
  );
};

// ------------------------------------------------------------------ page

const PropertyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null);
  const [notice, setNotice] = useState(null);
  const [property, setProperty] = useState(null);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const load = useCallback(() => {
    airbnbApi.getProperty(id).then(setProperty).catch(setError);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Images/policies feed the step blockers, so refresh both after a change
  const refresh = () => {
    load();
    setRefreshKey(k => k + 1);
  };

  if (!property) return <div className="pad20"><ErrorAlert error={error} />{!error && <PageLoader />}</div>;

  const pmPath = `/airbnb/property-managers/${property.property_manager_id}`;

  const disableAirbnb = () => {
    const extra = property.classification === 'multi-unit' ? ' Its enabled room types are switched off first.' : '';
    if (!window.confirm(`Deactivate "${property.name}" on Airbnb? Guests can no longer book it through Airbnb.${extra}`)) return;
    setBusy('disable');
    setError(null);
    airbnbApi.disableAirbnb(id)
      .then(() => { setNotice('Airbnb deactivated for this property.'); refresh(); })
      .catch(setError)
      .finally(() => setBusy(null));
  };

  const deleteProperty = () => {
    if (property.airbnb_enabled) {
      setError({ message: 'Deactivate Airbnb for this property first — NextPax only deletes properties that are not enabled on any channel.' });
      return;
    }
    if (property.unit_types_count) {
      setError({ message: `Remove its ${property.unit_types_count} room type(s) first — NextPax only deletes properties without children.` });
      return;
    }
    if (!window.confirm(`Remove "${property.name}" from NextPax and RedChannel? This can't be undone.`)) return;
    setBusy('delete');
    setError(null);
    airbnbApi.deleteProperty(id)
      .then(() => navigate(property.parent_id ? `/airbnb/properties/${property.parent_id}` : pmPath))
      .catch(err => { setError(err); setBusy(null); });
  };
  const isUnitType = property.classification === 'unit-type';
  const isMultiUnit = property.classification === 'multi-unit';

  return (
    <div className="container-fluid pad20 text-start">
      <div className="margbot10 fnt13">
        <Link to="/airbnb">Property managers</Link> / <Link to={pmPath}>Property manager</Link>
        {isUnitType && <> / <Link to={`/airbnb/properties/${property.parent_id}`}>{property.parent_name || property.hotel_name}</Link></>}
        {' / '}{property.name}
      </div>
      <div className="d-flex justify-content-between align-items-center margbot15">
        <h4 className="mb-0">
          {property.name} <span className="fnt14 text-muted">{isUnitType ? `room type of ${property.hotel_name}` : property.hotel_code}</span>
        </h4>
        <div className='mb-2'>
          {property.airbnb_enabled && (
            <Button size="sm" variant="outline-warning" className="me-2" disabled={!!busy} onClick={disableAirbnb}>
              {busy === 'disable' ? <Spinner animation="border" size="sm" /> : 'Deactivate on Airbnb'}
            </Button>
          )}
          <Link to={`/airbnb/properties/${id}/edit`} className="btn btn-outline-primary btn-sm me-2">Edit</Link>
          <Button size="sm" variant="outline-danger" disabled={!!busy} onClick={deleteProperty}>
            {busy === 'delete' ? <Spinner animation="border" size="sm" /> : 'Remove property'}
          </Button>
        </div>
      </div>
      <ErrorAlert error={error} onClose={() => setError(null)} />
      <Notice notice={notice} onClose={() => setNotice(null)} />

      <Card className="margbot15 mt-2">
        <Card.Body>
          <Row>
            <Info label="NextPax property ID">{property.nextpax_property_id || <span className="text-muted">not created yet</span>}</Info>
            <Info label="Type">{property.type_code} · {property.classification}</Info>
            <Info label="Airbnb onboarding">{property.onboarding_mode === 'new' ? 'New listing' : 'Takeover'}</Info>
            {isUnitType && <Info label="Units (rooms)">{property.number_of_units}</Info>}
            <Info label="Max occupancy">{property.max_occupancy}</Info>
            <Info label="Currency">{property.base_currency}</Info>
            <Info label="License code">{property.license_code}</Info>
            <Info label="Descriptions">
              {(property.descriptions || []).map(d => `${d.typeCode}/${d.language}`).join(', ')}
            </Info>
            <Info label="Airbnb listing ID">
              {property.airbnb_listing_id
                || <span className={property.listing_id_required ? 'text-danger' : 'text-muted'}>
                  {property.listing_id_required ? 'required for takeover' : 'not needed'}
                </span>}
            </Info>
            <Info label="Airbnb host ID">{property.airbnb_host_id || <span className="text-muted">property manager's</span>}</Info>
            <Info label="Airbnb policy">{property.airbnb_channel_policy_id || <span className="text-muted">property manager's</span>}</Info>
          </Row>
        </Card.Body>
      </Card>

      <Card className="margbot15 mt-2">
        <Card.Header className="bld">Airbnb channel status</Card.Header>
        <Card.Body>
          <Row>
            <Info label="Airbnb">
              {property.airbnb_enabled && <Badge bg="success">enabled</Badge>}
              {!property.airbnb_enabled && property.airbnb_disabled_at && (
                <span><Badge bg="secondary">deactivated</Badge> <span className="fnt12">{formatTime(property.airbnb_disabled_at)}</span></span>
              )}
              {!property.airbnb_enabled && !property.airbnb_disabled_at && <Badge bg="light" text="dark">not enabled</Badge>}
            </Info>
            <Info label="Build status"><StatusBadge status={property.airbnb_build_status} /></Info>
            <Info label="Channel property status">{property.airbnb_channel_property_status}</Info>
            <Info label="Listing URL">
              {property.airbnb_channel_property_url && (
                <a href={property.airbnb_channel_property_url} target="_blank" rel="noreferrer">Open on Airbnb</a>
              )}
            </Info>
            <Info label="Last checked">{property.airbnb_status_checked_at && formatTime(property.airbnb_status_checked_at)}</Info>
            {property.airbnb_last_job_error && (
              <Info label="Last job error" md={12}><span className="text-danger">{property.airbnb_last_job_error}</span></Info>
            )}
          </Row>
          <div className="fnt12 text-muted">Refreshed by the "Check channel status" step.</div>
        </Card.Body>
      </Card>

      <Card className="margbot15 mt-2">
        <Card.Header className="bld">Flow A — property steps</Card.Header>
        <Card.Body>
          <FlowASteps subjectPath={`properties/${id}`} onChange={load} refreshKey={refreshKey} />
        </Card.Body>
      </Card>

      {isMultiUnit && <RoomTypes property={property} onChange={refresh} />}
      <AirbnbRequirements classification={property.classification} />
      <Images propertyId={id} onChange={refresh} roomName={isUnitType ? property.name : null} />
      {isUnitType
        ? <div className="fnt12 text-muted margbot15">Cancellation policies are managed on the hotel property.</div>
        : <CancellationPolicies propertyId={id} onChange={refresh} />}
    </div>
  );
};

export default PropertyDetail;
